import {
  FetchProductsFilters,
  ProductCategoryID,
  ProductClientID,
  ProductID,
  ProductSku,
  ProductUUID
} from './productsTypes';

import { LifestyleID } from '../lifestyles/lifestylesTypes';
import { MaterialID } from '../materials/materialsTypes';
import { MatchResultUUID } from '../matchResults/matchResultsTypes';
import { TeamNanoID } from '../teams/teamsTypes';

export class ProductCache {
  static indexCacheKey() {
    return 'products';
  }

  static groupedByDetectedIdProductsCacheKey() {
    return 'grouped-by-detected-id-products';
  }

  static gridCacheKey() {
    return 'products-grid';
  }

  static showCacheKey() {
    return 'product';
  }

  static downloadUrls(uuid: ProductUUID) {
    return `product-${uuid}-download-urls`;
  }

  static downloadFile(uuid: ProductUUID) {
    return `product-${uuid}-download`;
  }

  static showImageCacheKey() {
    return 'product-image';
  }

  static shortInfoCacheKey() {
    return 'product-short-info';
  }

  static categoriesCacheKey() {
    return 'productCategories';
  }

  static indexNameSkuTagsCacheKey(
    nameSkuTags: FetchProductsFilters['nameSkuTags']
  ) {
    return `products-${nameSkuTags}`;
  }

  static aiSearchDataCacheKey() {
    return 'products-ai-search-data';
  }

  static similarDataCacheKey() {
    return 'similar-products-data';
  }

  static elasticSearchCacheKey() {
    return 'products-elastic-search';
  }

  static elasticSearchSuggestCacheKey() {
    return 'products-elastic-search-suggest';
  }

  static cacheSuggestProductsCacheKey() {
    return 'cache-suggest-products';
  }

  static createProductDataCacheKey() {
    return 'create-product-data';
  }

  static notEmptyCategoriesCacheKey() {
    return 'notEmptyProductCategories';
  }

  static skuFilteredCacheKey(sku: ProductSku[]) {
    return `productSkuFiltered-${sku.join('-')}`;
  }

  static idsFilteredCacheKey(ids: ProductID[]) {
    return `productIdsFiltered-${ids.join('-')}`;
  }

  static categoriesLibraryCacheKey(clientIdsPart: string) {
    return `productCategoriesLibrary-${clientIdsPart}`;
  }

  static libraryCacheKey(clientId: ProductClientID) {
    return `productsLibrary-${clientId}`;
  }

  static companyLibraryCacheKey(companyNanoId: TeamNanoID) {
    return `company-products-library-${companyNanoId}`;
  }

  static previewCompanyLibraryCacheKey(companyNanoId: TeamNanoID) {
    return `preview-company-products-library-${companyNanoId}`;
  }

  static currentCompanyLibraryCacheKey() {
    return 'current-company-products-library';
  }

  static companyLibraryGridCacheKey(companyNanoId: TeamNanoID) {
    return `company-products-library-grid-${companyNanoId}`;
  }

  static currentCompanyLibraryGridCacheKey() {
    return 'current-company-products-library-grid';
  }

  static companyLibraryTotalCountCacheKey(companyNanoId: TeamNanoID) {
    return `company-products-library-total-count-${companyNanoId}`;
  }

  static stylesFilterCacheKey() {
    return 'productStylesFilter';
  }

  static stylesLibraryFilterCacheKey(clientIdsPart: string) {
    return `productStylesLibraryFilter-${clientIdsPart}`;
  }

  static materialsFilterCacheKey() {
    return 'productMaterialsFilter';
  }

  static materialsFilterBadgeCacheKey() {
    return 'productMaterialsFilterBadge';
  }

  static materialsLibraryFilterCacheKey(clientIdsPart: string) {
    return `productMaterialsLibraryFilter-${clientIdsPart}`;
  }

  static materialsSelectedFilterCacheKey() {
    return 'productMaterialsSelectedFilter';
  }

  static typesFilterCacheKey() {
    return 'productTypesFilter';
  }

  static typesLibraryFilterCacheKey(clientIdsPart: string) {
    return `productTypesLibraryFilter-${clientIdsPart}`;
  }

  static typesSelectedFilterCacheKey() {
    return 'productTypesSelectedFilter';
  }

  static seasonalFilterCacheKey() {
    return 'productSeasonalFilter';
  }

  static seasonalLibraryFilterCacheKey(clientIdsPart: string) {
    return `productSeasonalLibraryFilter-${clientIdsPart}`;
  }

  static seasonalSelectedFilterCacheKey() {
    return 'productSeasonalSelectedFilter';
  }

  static colorsFilterCacheKey() {
    return 'productColorsFilter';
  }

  static colorsLibraryFilterCacheKey(clientIdsPart: string) {
    return `productColorsLibraryFilter-${clientIdsPart}`;
  }

  static brandsFilterCacheKey() {
    return 'productBrandsFilter';
  }

  static brandsLibraryFilterCacheKey(clientIdsPart: string) {
    return `productBrandsLibraryFilter-${clientIdsPart}`;
  }

  static brandsSelectedFilterCacheKey() {
    return 'productBrandsSelectedFilter';
  }

  static matchResultProductsCacheKey(matchResultUuid: MatchResultUUID) {
    return `match-result-products-cache-key-${matchResultUuid}`;
  }

  static showQueryKey(productUuid: ProductUUID) {
    return ['product', productUuid];
  }

  static productCountQueryKey(id: ProductID) {
    return `product-count-${id}`;
  }

  static indexSelectCacheKey(fieldName: string) {
    return `product-select-${fieldName}`;
  }
  static defaultSelectCacheKey(fieldName: string) {
    return `product-select-default-${fieldName}`;
  }

  static lifestyleAssociatedAsset(lifestyleId: LifestyleID) {
    return `lifestyle-associated-assets-${lifestyleId}`;
  }

  static materialAssociatedAsset(materialId: MaterialID) {
    return `material-associated-assets-${materialId}`;
  }

  static categoryPreview(productCategoryId: ProductCategoryID) {
    return `category-preview-${productCategoryId}`;
  }

  static productsLastBasePath() {
    return `products-last-base-path`;
  }

  static aggregationsProductsCacheKey() {
    return 'aggregations-products';
  }
}
